/** The five points of the scale (DESIGN.md §3 "The score scale"). */
export type ScoreValue = 1 | 2 | 3 | 4 | 5;

export const ruleForBucket: Record<ScoreValue, string> = {
  1: 'border-b-2 border-negative',
  2: 'border-b border-negative',
  3: 'border-b border-rule-strong',
  4: 'border-b border-positive',
  5: 'border-b-2 border-positive',
};

/** The two semantic colours §3 allows, plus the neutral 3 — a judgement, never a warning. */
export type ScoreTone = 'negative' | 'neutral' | 'positive';

/**
 * §3's table read as **meaning** rather than as classes: which colour a bucket carries, with no
 * opinion about where that colour goes.
 *
 * ⚠️ **It exists because one surface puts the colour somewhere else.** Every table, card and chart
 * renders `Score` — an ink numeral with the bucket's coloured rule beneath it — while §6.6 and §6.7
 * render the rating as a **margin mark**, where a hairline under a 30px numeral reads as an underline
 * and the numeral itself carries the colour (`DESIGN.md` §3, *The score scale*). Two renderings of
 * one table is exactly how a 2 ends up positive on one screen, so both read this module and
 * `meaning.test.tsx` asserts the two records agree bucket by bucket.
 */
export const toneForBucket: Record<ScoreValue, ScoreTone> = {
  1: 'negative',
  2: 'negative',
  3: 'neutral',
  4: 'positive',
  5: 'positive',
};

/** An average is ruled by the nearest whole number; `Math.round` puts a .5 on the higher bucket. */
export function bucketFor(value: number): ScoreValue {
  const rounded = Math.min(5, Math.max(1, Math.round(value)));
  return rounded as ScoreValue;
}
